'use client';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import { HoverExpandCardProps } from './types';
import { Button } from '../../button';
import { Chip } from '../../chip';

import IconArrow from '@/assets/icons/iconArrow.svg';
import IconLinkButton from '@/assets/icons/iconLinkButton.svg';
import { AppImageClient } from '@/shared/components/appImage';
import { useIsMobile } from '@/shared/hooks';
import { classNames } from '@/shared/lib/classNames/classNames';

export const HoverExpandCard = ({
  backgroundSrc,
  imageData,
  mobileBackgroundSrc,
  mobileImageData: mobileImageDataProp,
  titleId,
  title,
  descriptionId,
  description,
  isHiddenDescription,
  buttonId,
  buttonContent,
  href,
  imgOrient = 'vertical',
  chipsContent,
  isLinkIcon,
  isButtonLinkIcon,
  variant,
  defaultMinHeight = 0,
  customClassNames = {},
  buttonHref,
  alt,
  iconButton = false,
  width,
  height,
  isButtonAnimated = true,
}: HoverExpandCardProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();

  const [minHeight, setMinHeight] = useState(defaultMinHeight);

  useEffect(() => {
    if (contentRef.current) {
      setMinHeight(
        Math.max(defaultMinHeight, contentRef.current.clientHeight + 40),
      );
    }
  }, [defaultMinHeight]);

  const bgImgClassName = classNames({
    cls: 'absolute top-0 right-0 z-[1] hidden lg:block',
    mods: {
      'transition-opacity duration-300 group-hover:opacity-0':
        isHiddenDescription,
      'h-auto w-full': imgOrient === 'horizontal',
      'h-full w-auto': imgOrient === 'vertical',
      'h-[270px]': variant === 'primary',
    },
    additional: [customClassNames.bgImg],
  });

  const mobileBgImgClassName = classNames({
    cls: 'relative -right-4 h-auto w-full lg:hidden',
    additional: [customClassNames.mobileBgImg],
  });

  const mobileImageData = mobileImageDataProp || imageData;

  const content = (
    <>
      {(imageData || backgroundSrc) && !isMobile && (
        <>
          {imageData ? (
            <AppImageClient
              {...imageData}
              className={bgImgClassName}
              alt={alt || ''}
            />
          ) : (
            <img className={bgImgClassName} src={backgroundSrc} alt={alt} />
          )}
        </>
      )}

      <div
        className={classNames({
          cls: 'relative flex h-full flex-col justify-end overflow-clip p-7 max-sm:w-full max-sm:p-4',
          additional: [customClassNames.outerContainer],
        })}
      >
        {(mobileImageData || backgroundSrc) && (
          <>
            {mobileImageData ? (
              <AppImageClient
                {...mobileImageData}
                className={mobileBgImgClassName}
                alt={alt || ''}
              />
            ) : (
              <img
                className={mobileBgImgClassName}
                src={mobileBackgroundSrc || backgroundSrc}
                alt={alt}
                width={width}
                height={height}
              />
            )}
          </>
        )}
        <div
          className={classNames({
            cls: 'z-10 flex flex-col max-sm:gap-2',
            mods: {
              'gap-7 transition-transform duration-300 group-hover:translate-y-0 lg:translate-y-full':
                isHiddenDescription,
              'gap-2': !isHiddenDescription,
            },
            additional: [customClassNames.contentContainer],
          })}
          ref={contentRef}
        >
          <div
            id={titleId}
            className={classNames({
              cls: 'flex items-center text-2xl font-medium tracking-[-0.01rem] text-white max-sm:text-xl max-sm:leading-6',
              mods: {
                'transition-transform duration-300 lg:-translate-y-full lg:group-hover:translate-y-0':
                  isHiddenDescription,
                'max-w-[350px] min-w-[350px]': variant !== 'primary',
                'max-w-[312px] min-w-[312px]': variant === 'primary',
              },
              additional: [customClassNames.title],
            })}
          >
            {title}
            {isLinkIcon && (
              <IconLinkButton className="transition-rotate w-5 rotate-[45deg] duration-300 group-hover:rotate-[0deg]" />
            )}
          </div>
          {!!description && (
            <div
              id={descriptionId}
              className={classNames({
                cls: 'text-base leading-6 font-normal tracking-[-0.010rem] max-sm:text-sm max-sm:tracking-[-0.008rem]',
                additional: [customClassNames.description],
              })}
            >
              {description}
            </div>
          )}
          {!!buttonContent && (
            <Button
              id={buttonId}
              ariaLabel={buttonContent as string}
              className={classNames({
                cls: 'flex w-[216px] items-center justify-center max-sm:w-full',
                additional: [customClassNames.buttonContent],
              })}
              variant="secondary"
              href={buttonHref}
              target="_blank"
            >
              {buttonContent}
              {isButtonLinkIcon && (
                <IconLinkButton
                  className={classNames({
                    cls: 'w-5',
                    mods: {
                      'transition-rotate rotate-[45deg] duration-300 group-hover:rotate-[0deg]':
                        isButtonAnimated,
                    },
                  })}
                />
              )}
            </Button>
          )}
          {!!iconButton && (
            <Button
              id={buttonId}
              className="bg-surface-base-blur group-hover:bg-surface-base-inverse-primary my-auto hidden h-[48px] min-w-[48px] items-center justify-center rounded-[12px] backdrop-blur-[12px] transition-colors duration-300 max-sm:h-[36px] max-sm:w-[36px] max-sm:rounded-[8px] sm:flex"
              ariaLabel="IconLinkButton"
              href={buttonHref}
              target="_blank"
            >
              <IconArrow className="text-text-base-primary h-[24px] w-[24px] rotate-[-45deg] transition-transform duration-300 group-hover:rotate-[0]" />
            </Button>
          )}
        </div>
        {!!chipsContent?.length && (
          <div
            className={classNames({
              cls: 'absolute top-0 z-[2] flex w-full flex-row flex-wrap items-center justify-start gap-3 pt-7 max-sm:relative max-sm:top-0',
              additional: [customClassNames.chipsContainer],
            })}
          >
            {chipsContent.map((label) => (
              <Chip
                label={label}
                className={customClassNames.chip}
                key={label}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );

  const commonClasses = classNames({
    cls: 'group relative max-sm:w-full max-sm:p-0 lg:h-[328px] rounded-20 bg-surface-base-secondary',
    mods: { 'h-[376px] lg:h-[376px]': variant === 'primary' },
    additional: [customClassNames.main],
  });

  return href ? (
    <Link
      href={href}
      className={commonClasses}
      style={{ minHeight: `${minHeight}px` }}
    >
      {content}
    </Link>
  ) : (
    <div className={commonClasses} style={{ minHeight: `${minHeight}px` }}>
      {content}
    </div>
  );
};
